import { DatePipe, DecimalPipe, Location } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, Subscription, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";

import { EventEmitter } from "@angular/core";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  OperationType,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { Exchange } from "../../model/domain/Exchange";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-exchange",
  templateUrl: "./exchange.component.html",
  styleUrls: ["./exchange.component.scss"],
})
export class ExchangeComponent implements OnInit, OnDestroy {
  dataChanged: EventEmitter<string>;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private snackBarService: SnackbarService,
    private authService: AuthService,
    private dialogService: DialogService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundComponent: DadosFundoComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  navigationSubscription: Subscription;

  PurchaseTypes: NamedType<number>[];
  TicketTypeEnum = TicketType;

  isEditing = false;
  canValidate = false;
  model: Exchange = null;

  ListPurchaseTypes: NamedType<number>[];
  isNotProduction = environment.name != 'production';

  validators = {
    resourceDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    dueDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    settlementDate: (value) => this.model.settlementDate != null && value != "",
    appealDate: (value) => this.model.appealDate != null && value != "",
    expirationDate: (value) => this.model.expirationDate != null && value != "",
    contractSaleSettlementDate: (value) =>
      this.model.contractSaleSettlementDate != null && value != "",
  };

  ngOnInit() {
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.model = new Exchange();

    this.frm.reset();
    this.frm.resetForm();

    this.model.operationDate = new Date();

    this.fundComponent.resetForm();
    this.annotationComponent.annotation = null;
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(ticketId: number = null) {
    this.model = new Exchange();
    const id =
      ticketId === null ? +this.route.snapshot.paramMap.get("id") : ticketId;
    this.model.command = "0";

    this.ListPurchaseTypes = [this.service.ListStandardOperationTypes()[0]];

    if (!this.model.operationTypeId)
      this.model.operationTypeId = OperationType.Application;
    this.PurchaseTypes = this.service.ListStandardOperationTypes();
    this.model.operationTypeId = this.ListPurchaseTypes[0].id;

    if (id) {
      this.service.Exchange.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.operationValue = this.decimalPipe.transform(
            this.model.operationValue,
            "1.0-2",
            "pt-BR"
          ) as any;
          this.model.currencyQuotation = this.decimalPipe.transform(
            this.model.currencyQuotation,
            "1.0-6",
            "pt-BR"
          ) as any;

          this.model.settlementDate = this.datePipe.transform(
            this.model.settlementDate,
            "dd/MM/yyyy"
          ) as any;
          this.model.appealDate = this.datePipe.transform(
            this.model.appealDate,
            "dd/MM/yyyy"
          ) as any;
          this.model.expirationDate = this.datePipe.transform(
            this.model.expirationDate,
            "dd/MM/yyyy"
          ) as any;
          this.model.contractSaleSettlementDate = this.datePipe.transform(
            this.model.contractSaleSettlementDate,
            "dd/MM/yyyy"
          ) as any;

          if (this.model.currencyAmount != null) {
            this.model.currencyAmount = this.decimalPipe.transform(
              this.model.currencyAmount,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          this.fundComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Exchange])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundComponent.validatePortfolioFields();
    const fundErrors = this.fundComponent.getErrors();

    if (f.invalid || errors.length > 0 || fundErrors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.operationValue = this.validator.getCurrency(data.operationValue);

    if (data.currencyAmount != null) {
      data.currencyAmount = this.validator.getCurrency(data.currencyAmount);
    }

    if (data.portfolioBranch != null) {
      data.portfolioBranch = this.validator.sanitize(data.portfolioBranch);
    }

    if (data.currencyQuotation != null) {
      data.currencyQuotation = this.validator.getCurrency(
        data.currencyQuotation
      );
    }

    data.settlementDate = new Date(
      data.settlementDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );
    data.appealDate = new Date(
      data.appealDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );
    data.expirationDate = new Date(
      data.expirationDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );

    if (data.contractSaleSettlementDate != null) {
      data.contractSaleSettlementDate = new Date(
        data.contractSaleSettlementDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    data = this.fundComponent.fillPortfolioData(
      data,
      this.fundComponent.portfolio
    );

    data.annotations = this.annotationComponent.annotation;

    const method =
      editing && !duplicate
        ? this.service.Exchange.updateTicket(this.model.id, data)
        : this.service.Exchange.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((exchange: Exchange) => {
          if (!editing) {
            this.model.id = exchange.id;
            this.model.operationDate = exchange.operationDate;
            this.model.statusId = exchange.statusId;
            this.model.accountManagerName = exchange.accountManagerName;
            this.model.accountManagerDocument = exchange.accountManagerDocument;
            this.model.ticketCutoffTypeId = exchange.ticketCutoffTypeId;
            this.model.portfolioFundId = exchange.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }
      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (cambio: Exchange) => {
            this.service.Exchange.forwardTicket(cambio.id).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(cambio.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Exchange.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open("Boleta atualizada com sucesso.", "OK");
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { boletaId: TicketType.Exchange },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  isSell(): boolean {
    return this.model.operationTypeId === OperationType.Redemption;
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canEdit(): boolean {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketExchange)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Exchange;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate(): boolean {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketExchange) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus(): boolean {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketExchange)
    );
  }
}
